'use client'

// The meadow. The octopus wish: grass, found rather than shown.
//
// A TSL port of the old animeGrass blades (GLSL ShaderMaterial, which the
// webgpu backend cannot run): camera-facing instanced quads masked by the
// same grassMask1k atlas (four blade silhouettes in quadrants), per-blade
// colour variation baked into attributes, wind in the vertex stage, and
// blades fading in only when the camera is near, which is why it stays
// cheap. Trial-gated behind ?meadow=1 until Sam signs it off.
//
// ── Where the grass goes ────────────────────────────────────────────────
//
// Painted, not placed. It used to be a 5x5 square around a hardcoded
// centre, which meant a perfectly square lawn and a code change every time
// it should move. Now it grows wherever the terrain is painted RED in
// vertex colours, so the shape is authored in Blender and this file never
// needs to know where the meadow is.
//
// Redness is r minus the strongest of g and b, so pure red is 1 and white
// is 0. Without that, every unpainted white vertex reads as full red and
// the whole 250-unit floor turns into a lawn.
//
// Blade count follows the painted AREA rather than being a constant: paint
// a bigger patch and it fills at the same density instead of thinning out.

import { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { MeshBasicNodeMaterial } from 'three/webgpu'
import {
  Fn, attribute, cameraPosition, float, mix, positionLocal, sin, smoothstep,
  texture, time, uv, vec2, vec3, step, mod, length, normalize, cross, uniform,
} from 'three/tsl'
import { useGLTF } from '@react-three/drei'
import { getKTX2 } from '../hooks/useModel'
import { mood } from '../../../lib/world/mood'

// Blades per square world unit. The old square was 3200 over 5x5, so this
// keeps the density it was signed off at.
const DENSITY = 128
// A floor so a tiny painted dab is still grass, and a ceiling so a slip of
// the brush in Blender cannot ask the GPU for a million quads.
//
// 24000 was the first guess and the first real paint blew straight through
// it: 368 painted units wanted 47000 and got capped to half density, which
// is a thin lawn rather than a meadow. These are unlit quads with no
// normals and most of them are faded out by distance, so the cost is the
// vertex stage and little else; 48000 covers the meadow as painted with
// room to grow it.
const MIN_BLADES = 400
const MAX_BLADES = 48000
// How red a vertex has to be before it counts as painted.
const RED_AT = 0.25
const BLADE_W = 0.34
const BLADE_H = 0.55
// The near-only rule: invisible past FADE_END, full by FADE_START.
const FADE_START = 22
const FADE_END = 32

const BASE = '#226922'
const SECONDARY = '#669966'
const TIP = '#88bb66'

const _mask = new Map()
function maskTexture(ktx2, url) {
  if (!_mask.has(url)) {
    _mask.set(url, ktx2.loadAsync(url).then((tex) => {
      tex.colorSpace = THREE.NoColorSpace
      tex.userData.shared = true
      return tex
    }))
  }
  return _mask.get(url)
}

/**
 * Read one channel of a colour attribute as 0..1.
 *
 * Vertex colours arrive from Blender as normalised UNSIGNED_SHORT, and a
 * raw read gives 0..65535, which makes every vertex look violently red.
 * Handled by hand rather than trusted to the attribute flag, because that
 * flag depends on the exporter.
 */
function channel(attr, i, c) {
  const v = attr.array[i * attr.itemSize + c]
  if (attr.array instanceof Uint16Array) return v / 65535
  if (attr.array instanceof Uint8Array) return v / 255
  return v
}

/** How painted this vertex is: pure red 1, white 0, unpainted 0. */
function redness(attr, i) {
  const r = channel(attr, i, 0)
  const g = channel(attr, i, 1)
  const b = channel(attr, i, 2)
  return Math.max(0, r - Math.max(g, b))
}

/**
 * Scatter points across the red parts of a mesh.
 *
 * Area-weighted triangle sampling: a triangle's chance of being picked is
 * its area times how red it is, so a big faint patch and a small vivid one
 * both come out at a sensible density and the edges of the painted area
 * thin out rather than stopping dead.
 *
 * Returns offsets in the same space the floor is drawn in, terrain height
 * included, because the blades have to sit on the ground rather than at
 * y=0 on a floor that is not flat.
 */
function scatterOnRed(geo, matrix = null) {
  const pos = geo.getAttribute('position')
  const col = geo.getAttribute('color')
  if (!pos || !col) return null

  const idx = geo.getIndex()
  const triCount = idx ? idx.count / 3 : pos.count / 3
  const at = (t, corner) => (idx ? idx.getX(t * 3 + corner) : t * 3 + corner)

  // One pass to weigh every triangle, so the second pass is a lookup.
  const weights = new Float64Array(triCount)
  const a = new THREE.Vector3()
  const b = new THREE.Vector3()
  const c = new THREE.Vector3()
  let total = 0
  let painted = 0

  for (let t = 0; t < triCount; t++) {
    const i0 = at(t, 0)
    const i1 = at(t, 1)
    const i2 = at(t, 2)
    const red = (redness(col, i0) + redness(col, i1) + redness(col, i2)) / 3
    if (red < RED_AT) continue
    a.fromBufferAttribute(pos, i0)
    b.fromBufferAttribute(pos, i1)
    c.fromBufferAttribute(pos, i2)
    const area = b.clone().sub(a).cross(c.clone().sub(a)).length() / 2
    const w = area * red
    weights[t] = w
    total += w
    painted += area
  }
  if (total <= 0) return null

  const count = Math.max(MIN_BLADES, Math.min(MAX_BLADES, Math.round(painted * DENSITY)))
  // Cumulative weights, so picking a triangle is a binary search rather
  // than a walk over every triangle for every blade.
  const cum = new Float64Array(triCount)
  let running = 0
  for (let t = 0; t < triCount; t++) { running += weights[t]; cum[t] = running }

  const offset = new Float32Array(count * 3)
  const p = new THREE.Vector3()
  for (let n = 0; n < count; n++) {
    const target = Math.random() * total
    let lo = 0
    let hi = triCount - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (cum[mid] < target) lo = mid + 1
      else hi = mid
    }
    a.fromBufferAttribute(pos, at(lo, 0))
    b.fromBufferAttribute(pos, at(lo, 1))
    c.fromBufferAttribute(pos, at(lo, 2))
    // Uniform barycentric point in the triangle.
    let u = Math.random()
    let v = Math.random()
    if (u + v > 1) { u = 1 - u; v = 1 - v }
    p.copy(a)
      .addScaledVector(b.clone().sub(a), u)
      .addScaledVector(c.clone().sub(a), v)
    if (matrix) p.applyMatrix4(matrix)
    offset[n * 3] = p.x
    offset[n * 3 + 1] = p.y
    offset[n * 3 + 2] = p.z
  }

  // Where the meadow IS, derived from the paint rather than guessed at.
  // The terrain is draco-compressed, so this cannot be read offline; the
  // sim needs a literal POI, and this log is where that number comes from.
  let minX = Infinity; let maxX = -Infinity
  let minZ = Infinity; let maxZ = -Infinity
  let sumX = 0; let sumZ = 0; let sumY = 0
  for (let n = 0; n < count; n++) {
    const x = offset[n * 3]; const y = offset[n * 3 + 1]; const z = offset[n * 3 + 2]
    sumX += x; sumY += y; sumZ += z
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (z < minZ) minZ = z
    if (z > maxZ) maxZ = z
  }
  const bounds = {
    cx: sumX / count, cy: sumY / count, cz: sumZ / count,
    minX, maxX, minZ, maxZ,
    // The radius the distance cull uses: half the diagonal, so a camera
    // outside it plus the fade distance genuinely cannot see a blade.
    radius: Math.hypot(maxX - minX, maxZ - minZ) / 2,
  }
  return { offset, count, painted, bounds }
}

export function Meadow() {
  const gl = useThree((s) => s.gl)
  const [mask, setMask] = useState(null)
  // The same terrain the height field is built from, read for its vertex
  // colours. useGLTF caches, so this is not a second download.
  const { nodes } = useGLTF('/models/terrain.glb')

  useEffect(() => {
    let alive = true
    maskTexture(getKTX2(gl), '/textures/grassMask1k.ktx2')
      .then((t) => { if (alive) setMask(t) })
      .catch((err) => console.warn('[meadow] mask failed, no grass:', err?.message))
    return () => { alive = false }
  }, [gl])

  const built = useMemo(() => {
    const floor = nodes?.floor
    if (!floor?.geometry) return null
    // NO transform, deliberately, and this was a bug the first time round.
    //
    // The floor node in the glb carries a translation (-0.42, -0.49, 0.13)
    // and applying it is the obviously correct thing to do. But TestMap
    // renders the visible ground straight off nodes.floor.geometry with no
    // transform at all, so the ground everybody sees ignores that
    // translation. Applying it here dropped every blade half a unit and the
    // grass grew up through the floor.
    //
    // Match what is drawn, not what the file says. If TestMap ever starts
    // transforming the floor, this has to follow it. (since 29 Oct the floor
    // sits at the origin in Kiwi_world.blend and the node carries no
    // translation, so the two agree either way)
    const scattered = scatterOnRed(floor.geometry, null)
    if (!scattered) {
      console.warn('[meadow] no red vertex colours on the terrain, no grass')
      return null
    }
    const { offset, count, painted, bounds } = scattered
    if (process.env.NODE_ENV !== 'production') console.log(
      `[meadow] ${count} blades over ${painted.toFixed(1)} painted units | ` +
      `centre ${bounds.cx.toFixed(2)}, ${bounds.cy.toFixed(2)}, ${bounds.cz.toFixed(2)} | ` +
      `radius ${bounds.radius.toFixed(1)}`,
    )

    const plane = new THREE.PlaneGeometry(BLADE_W, BLADE_H)
    plane.translate(0, BLADE_H / 2, 0)
    const geo = new THREE.InstancedBufferGeometry().copy(plane)
    geo.instanceCount = count
    // An unlit quad needs no normals, and webgpu allows 8 vertex buffers:
    // five separate instanced attributes blew straight past it.
    geo.deleteAttribute('normal')

    // Packed per-blade character: rand, colourMix, tipVar, mask quadrant.
    const blade = new Float32Array(count * 4)
    for (let i = 0; i < count; i++) {
      blade[i * 4] = Math.random()
      blade[i * 4 + 1] = Math.random()
      blade[i * 4 + 2] = 0.3 + Math.random() * 0.7
      blade[i * 4 + 3] = Math.floor(Math.random() * 4)
    }
    geo.setAttribute('offset', new THREE.InstancedBufferAttribute(offset, 3))
    geo.setAttribute('blade', new THREE.InstancedBufferAttribute(blade, 4))
    return { geo, count, bounds }
  }, [nodes])

  const material = useMemo(() => {
    if (!mask) return null
    const mat = new MeshBasicNodeMaterial()
    mat.transparent = true
    mat.depthWrite = false
    mat.alphaTest = 0.05

    const off = attribute('offset', 'vec3')
    const blade = attribute('blade', 'vec4')
    const rnd = blade.x
    const cMix = blade.y
    const tVar = blade.z
    const q = blade.w

    mat.positionNode = Fn(() => {
      const scale = rnd.mul(0.3).add(1)
      // Face the camera around Y only, so blades never lie down.
      const toCam = normalize(vec3(cameraPosition.x.sub(off.x), 0, cameraPosition.z.sub(off.z)))
      const right = normalize(cross(vec3(0, 1, 0), toCam))
      const local = right.mul(positionLocal.x.mul(scale))
        .add(vec3(0, 1, 0).mul(positionLocal.y.mul(scale)))
      // Wind: the whole blade leans, the lean scales with height.
      const angle = sin(time.mul(0.4).add(rnd.mul(6.28))).mul(0.2)
      const sway = vec2(sin(angle), angle.cos()).mul(local.y).mul(0.2)
      return off.add(local).add(vec3(sway.x, 0, sway.y))
    })()

    // Four blade silhouettes live in the mask's quadrants; rand picked one
    // per blade. The 1.1 - v flip is carried over from the GLSL original,
    // whose mask this is.
    const quadOff = vec2(mod(q, 2), step(2, q))
    const maskUV = vec2(uv().x, float(1.1).sub(uv().y)).mul(0.5).add(quadOff.mul(0.5))
    const alpha = texture(mask, maskUV).r

    const dist = length(off.xz.sub(cameraPosition.xz))
    const near = float(1).sub(smoothstep(float(FADE_START), float(FADE_END), dist))
    const bottomFade = smoothstep(float(0), float(0.95), uv().y)
    mat.opacityNode = alpha.mul(bottomFade).mul(near)

    const base = vec3(...new THREE.Color(BASE).toArray())
    const secondary = vec3(...new THREE.Color(SECONDARY).toArray())
    const tip = vec3(...new THREE.Color(TIP).toArray())
    const mixed = mix(base, secondary, cMix)
    const tipInfluence = smoothstep(float(0.3), float(1), uv().y).mul(0.4).mul(tVar)
    const day = mix(mixed, tip, tipInfluence)
    // Unlit material, so night is applied by hand or the patch glows
    // daylight-green under the moon: dimmed and pulled toward moon blue
    // via the shared mood uniform, same idea as the painted ground pools.
    mat.colorNode = mix(day, day.mul(vec3(0.4, 0.5, 0.95)).mul(0.5), mood.nightness)

    return mat
  }, [mask])

  // ── The cull ──────────────────────────────────────────────────────────
  //
  // 47000 blades is a real cost and almost all of it is waste: the fade
  // makes a blade invisible past FADE_END, but the vertex stage still runs
  // for every one of them every frame whether anybody is near or not. He
  // is the only one who goes here and most visitors never will, so the
  // whole mesh switches off unless the camera is close enough to see a
  // single blade. One distance check a frame, and the meadow costs nothing
  // for the ninety-odd percent of the time nobody is standing in it.
  const ref = useRef()
  useFrame(({ camera }) => {
    const mesh = ref.current
    if (!mesh || !built) return
    const { cx, cz, radius } = built.bounds
    const dx = camera.position.x - cx
    const dz = camera.position.z - cz
    // Radius plus the fade distance, plus a little, so the patch is never
    // switched on inside the frame you could first see it.
    const reach = radius + FADE_END + 6
    mesh.visible = dx * dx + dz * dz < reach * reach
  })

  if (!material || !built) return null
  return (
    <instancedMesh
      ref={ref}
      args={[built.geo, material, built.count]}
      frustumCulled={false}
    />
  )
}
